import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ExportedResourceDetail, Migration } from '../../../types';
import { getScheduleDisplay, getNextRunDisplay } from '../../../utils/cron';
import { formatUtcTimestampLocal, toFriendlyName } from '../../../utils/formatters';
import { ui } from '../../../config/uiSizes';
import { useAuth } from '../../../hooks/auth';
import { useFetchExportedResources } from '../../../hooks/api';
import { getTypeLabelByApiName } from '../../../utils/resourceTypes';
import { isContextualFailureMessagingEnabled, isRollbackEnabled } from '../../../utils/appEnvironment';
import { Tooltip } from '../../Tooltip';
import Spinner from '../../migratorDashboard/Spinner';
import { SnapshotHistoryTable } from '../Snapshot/SnapshotHistoryTable';
import { MigrationHistoryTable } from '../Migration/MigrationHistoryTable';
import MigrationWarningsBanner from '../../Migrations/MigrationWarningsBanner';
import NotFullyMigratedBanner from '../../Migrations/NotFullyMigratedBanner';
import TargetReadinessPanel from '../../Migrations/TargetReadinessPanel';
import CollapsibleFindingsSection from '../../Migrations/CollapsibleFindingsSection';
import { FindingsDisclosureProvider } from '../../Migrations/FindingsDisclosureContext';
import FindingsDisclosureToolbar from '../../Migrations/FindingsDisclosureToolbar';
import MigrationPreviewPanel from '../../Migrations/MigrationPreviewPanel';
import RunRealImportButton from '../../Migrations/RunRealImportButton';
import {
  isDivisionWarning,
  isReconciliationWarning,
  isManualRemediationWarning,
  getManualStepsWarnings,
  getTargetReadinessWarnings,
} from '../../../utils/migrationWarnings';
import { computeImportResultSummary } from '../../../utils/importResultSummary';
import { classifyRetrySafety } from '../../../utils/retrySafety';
import retryImport from '../../../services/Migration/retryImportService';
import { useToast } from '../../Toast/ToastProvider';
import { logger } from '../../../utils/logger';

const ROLLBACK_WINDOW_DAYS = 5;

interface AttentionModalContentProps {
  job: Migration;
  onClose?: () => void;
  onSyncNow?: () => void;
  syncNowLoading?: boolean;
}

interface DisplayResourceEntry {
  key: string;
  primary: string;
  secondary?: string | undefined;
}

export const AttentionModalContent: React.FC<AttentionModalContentProps> = ({ job, onClose: _onClose, onSyncNow, syncNowLoading = false }) => {
  const navigate = useNavigate();
  // Feature flag — contextual failure messaging (Dev only). Phases 2 & 3 render behind this flag.
  const contextualFailureMessagingEnabled = isContextualFailureMessagingEnabled();
  // Feature flag — rollback UI. Now generally available (GA) across all environments;
  // governed by the single isRollbackEnabled() helper so it can be re-gated centrally.
  // A dry-run (preview) applied nothing, so there is nothing to roll back — hide rollback for it.
  const isDryRunPreview = !job.isSnapshot && job.job3Import?.dryRun === true;
  const rollbackEnabled = isRollbackEnabled() && !isDryRunPreview;
  const { accessToken } = useAuth();
  const { showToast } = useToast();
  const scheduleDisplay = getScheduleDisplay(job.snapshotCronExpression);
  const nextRunDisplay = getNextRunDisplay(job.snapshotCronExpression);
  const [isCopied, setIsCopied] = React.useState(false);
  const [isRetrying, setIsRetrying] = React.useState(false);

  // Rollback availability window
  const rollbackCompletedAt = job.job3Import?.completedAtUtc ?? job.updatedAtUtc ?? null;
  const daysSinceCompletion = React.useMemo(() => {
    if (!rollbackCompletedAt) return null;
    return Math.floor((Date.now() - new Date(rollbackCompletedAt).getTime()) / (1000 * 60 * 60 * 24));
  }, [rollbackCompletedAt]);
  const rollbackWindowExpired = daysSinceCompletion !== null && daysSinceCompletion > ROLLBACK_WINDOW_DAYS;
  const daysRemainingInWindow = daysSinceCompletion !== null
    ? Math.max(0, ROLLBACK_WINDOW_DAYS - daysSinceCompletion)
    : null;

  const {
    exportedResources,
    loading: loadingResources,
    error: exportedResourcesError
  } = useFetchExportedResources(accessToken, job.id);

  const resourcesByType = React.useMemo(() => {
    if (!exportedResources?.resourceTypes) return new Map<string, DisplayResourceEntry[]>();

    const map = new Map<string, DisplayResourceEntry[]>();

    const isWildcardValue = (val: string): boolean =>
      val === '*' || val.toLowerCase().includes('all resources of this type');

    const getFallbackEntries = (names: string[]): DisplayResourceEntry[] =>
      names.map((name, idx) => ({
        key: `${name}-${idx}`,
        primary: isWildcardValue(name) ? 'Attempted to export all resources' : name
      }));

    Object.entries(exportedResources.resourceTypes).forEach(([type, names]) => {
      const detailsForType = exportedResources.resourceDetails?.[type];

      if (Array.isArray(detailsForType) && detailsForType.length > 0) {
        const detailEntries = detailsForType.map((detail: ExportedResourceDetail, idx: number) => {
          const isWildcard =
            isWildcardValue(detail.key ?? '') || isWildcardValue(detail.displayName ?? '');
          return {
            key: detail.key ?? `${type}-${idx}`,
            primary: isWildcard
              ? 'Attempted to export all resources'
              : detail.displayName ?? detail.key ?? String(idx),
            secondary: isWildcard ? undefined : (detail.groupName ?? undefined)
          };
        });
        map.set(type, detailEntries);
      } else if (Array.isArray(names) && names.length > 0) {
        map.set(type, getFallbackEntries(names as string[]));
      }
    });

    return map;
  }, [exportedResources]);

  // Determine which job has errors and the appropriate action
  const getErrorInfo = () => {
    // Covers both explicit 'ImportFailed'/'ValidationFailed' and the generic 'Failed'
    // status the backend emits when an import job errors out.
    const isImportFailure =
      !job.isSnapshot &&
      (
        job.status === 'ImportFailed' ||
        job.status === 'ValidationFailed' ||
        ((job.status === 'Failed' || job.status === 'failed' || job.status === 13 || Number(job.status) === 13) && (job.job3Import?.errors?.length ?? 0) > 0) ||
        // PartiallyCompleted (enum 10): some resources imported, others failed.
        // Surface the per-resource failures the same way as a full import failure.
        ((job.status === 'PartiallyCompleted' || Number(job.status) === 10) && (job.job3Import?.errors?.length ?? 0) > 0)
      );

    if (isImportFailure) {
      const errors = (job.job3Import?.errors?.length ?? 0) > 0
        ? job.job3Import!.errors
        : (job.job1Export?.errors?.length ?? 0) > 0
          ? job.job1Export!.errors
          : (job.job2Transform?.errors?.length ?? 0) > 0
            ? job.job2Transform!.errors
            : [];

      // Determine failure phase for contextual messaging (Phases 2 & 3)
      const rawPhase = job.job3Import?.failurePhase ?? null;
      const failurePhase: 'planning' | 'execution' | null =
        rawPhase === 1 ? 'planning' : rawPhase === 2 ? 'execution' : null;

      // Planning failures and execution failures both route the user back to Approve Export
      // so they can review/re-approve their selections before retrying the migration.
      return {
        stage: 'Import',
        failurePhase,
        errors,
        buttonText: 'Edit Approve Export',
        route: '/ApproveExportPage',
        state: { job }
      };
    }
    if (job.job1Export?.errors && job.job1Export.errors.length > 0 && job.isSnapshot) {
      // Snapshots cannot be edited, only stopped/resumed from the dashboard
      return {
        stage: 'Export',
        errors: job.job1Export.errors,
        buttonText: null,
        route: null,
        navigateHandler: null
      };
    } else if (job.job1Export?.errors && job.job1Export.errors.length > 0 && !job.isSnapshot) {
      return {
        stage: 'Export',
        errors: job.job1Export.errors,
        buttonText: 'Edit Migration Settings',
        route: '/MigrationSetup',
        state: {
          job,
          migrationData: {
            name: job.name,
            sourceOrgName: job.sourceOrgName,
            targetOrgName: job.targetOrgName,
            sourceOrgId: job.sourceOrgId,
            targetOrgId: job.targetOrgId,
            resources: job.resources,
            id: job.id,
            isPrePopulatedForm: true
          }
        }
      };
    }

    if (job.job2Transform?.errors && job.job2Transform.errors.length > 0) {
      return {
        stage: 'Transformation',
        errors: job.job2Transform.errors,
        buttonText: 'Go to Transformation Page',
        route: '/ApproveTransformationsPage',
        state: { job }
      };
    }
    if (job.job3Import?.errors && job.job3Import.errors.length > 0) {
      return {
        stage: 'Import',
        errors: job.job3Import.errors,
        buttonText: 'Go to Approve Export',
        route: '/ApproveExportPage',
        state: { job }
      };
    }

    if (job.status === 'RollbackFailed' || job.status === 'RollbackPartiallyCompleted') {
      const errors =
        (job.job5RollbackApply?.errors?.length ?? 0) > 0
          ? job.job5RollbackApply!.errors
          : (job.job4RollbackSetup?.errors?.length ?? 0) > 0
            ? job.job4RollbackSetup!.errors
            : [];
      return {
        stage: 'Rollback',
        errors,
        buttonText: null,
        route: null,
        navigateHandler: null
      };
    }

    return null;
  };

  const getWarningInfo = () => {
    // Failed status with no errors but warnings — e.g. CompletedNoChanges (nothing created/updated)
    const hasNoErrors =
      (job.job3Import?.errors?.length ?? 0) === 0 &&
      (job.job2Transform?.errors?.length ?? 0) === 0 &&
      (job.job1Export?.errors?.length ?? 0) === 0;

    const isFailed =
      job.status === 'failed' ||
      job.status === 'Failed' ||
      job.status === 13 ||
      Number(job.status) === 13;

    if (
      isFailed &&
      hasNoErrors &&
      (job.job3Import?.warnings?.length ?? 0) > 0
    ) {
      return {
        stage: 'Import',
        warnings: job.job3Import!.warnings as string[]
      };
    }
    return null;
  };

  const errorInfo = getErrorInfo();
  const warningInfo = !errorInfo ? getWarningInfo() : null;
  const isPlanningFailure = contextualFailureMessagingEnabled && errorInfo?.failurePhase === 'planning';
  const isExecutionFailure = contextualFailureMessagingEnabled && errorInfo?.failurePhase === 'execution';

  // Division-remapping warnings ([DIV-MAP] / [DIV-PRUNE] / [RESOURCE-CHECK] division conflicts)
  // surfaced from the transform and import jobs. These are shown regardless of overall status so a
  // skipped division_id remap or an unmatched org-scoped resource is never silently hidden behind a
  // "completed" state.
  const divisionWarnings = React.useMemo(
    () =>
      [
        ...(job.job2Transform?.warnings ?? []),
        ...(job.job3Import?.warnings ?? []),
      ].filter((w) => isDivisionWarning(w) && !isManualRemediationWarning(w)),
    [job.job2Transform?.warnings, job.job3Import?.warnings]
  );

  // End-to-end import reconciliation ([RECONCILIATION]) warnings. When one or more selected
  // resources were requested but never landed in the target org, the Importer appends a rollup +
  // per-resource-type breakdown here so the "Not fully migrated" state is never hidden behind a
  // "completed" badge.
  // A dry-run applies nothing, so post-apply accounting ("not fully migrated", "X of Y imported/failed",
  // manual finish steps) does not apply and would contradict the preview — suppress it for a preview.
  const reconciliationWarnings = React.useMemo(
    () => (isDryRunPreview ? [] : (job.job3Import?.warnings ?? []).filter(isReconciliationWarning)),
    [job.job3Import?.warnings, isDryRunPreview]
  );

  // Target-org readiness prerequisites ([TARGET-READINESS] pre-flight findings + [FLOW-TYPE-DEPRECATED]
  // from the export job): missing TTS voices, duplicate names, division cap, emergency-number
  // collisions, deprecated flow types. Surfaced in their own actionable panel.
  const readinessWarnings = React.useMemo(
    () => getTargetReadinessWarnings(job),
    [job]
  );

  // Partial-success clarity — "X of Y resources imported" derived from the authoritative per-resource
  // import results (populated when continue-on-error is enabled). Shown alongside the errors so a
  // partial import reports how much DID land, not only what failed. Null when no per-resource
  // breakdown exists (e.g. a planning-phase failure that never attempted individual resources).
  const importResultSummary = React.useMemo(
    () => (isDryRunPreview ? null : computeImportResultSummary(job.job3Import?.resourceResults)),
    [job.job3Import?.resourceResults, isDryRunPreview]
  );

  // Warnings shown under "Manual steps required" — every completion warning that isn't a pure
  // division-remapping warning (cascade exclusions plus any other non-division warnings), so
  // nothing is silently dropped and each warning appears exactly once.
  const completionWarnings = React.useMemo(
    () => (isDryRunPreview ? [] : getManualStepsWarnings(job)),
    [job, isDryRunPreview]
  );

  // Header warning styling applies for both classic warning-info states and completed
  // migrations that carry manual-remediation warnings.
  const isWarningState = !!warningInfo || completionWarnings.length > 0;
  const warningHeading = warningInfo ? `${warningInfo.stage} Warning` : 'Completed — Manual Steps Required';
  const warningSubtext = warningInfo
    ? 'Completed with warnings'
    : 'Completed — some resources require manual steps';

  const typeLabel = job.isSnapshot ? 'Snapshot' : 'Migration';

  const handleCopyErrorLog = async () => {
    if (!errorInfo) return;

    const errorText = Array.isArray(errorInfo.errors)
      ? errorInfo.errors.join('\n')
      : errorInfo.errors;

    if (!errorText) return;

    try {
      await navigator.clipboard.writeText(errorText);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      logger.error('Failed to copy error log', err instanceof Error ? err : new Error(String(err)));
    }
  };

  // Copies the pre-migration snapshot blob path for auditing. Rollback restores
  // the target org from this snapshot, so surfacing its location lets an operator
  // verify or retrieve the exact artifact a rollback would use.
  const handleCopySnapshotPath = async () => {
    const path = job.snapshotBlobUrl;
    if (!path) return;
    try {
      await navigator.clipboard.writeText(path);
      showToast('Snapshot path copied to clipboard', 'success');
    } catch (err) {
      logger.error('Failed to copy snapshot path', err instanceof Error ? err : new Error(String(err)));
      showToast('Could not copy the snapshot path', 'error');
    }
  };

  const handleNavigateToFix = () => {
    if (!errorInfo?.route) return;

    navigate(errorInfo.route, errorInfo.state ? { state: errorInfo.state } : undefined);
  };

  const handleRetryImport = async () => {
    if (!accessToken || isRetrying) return;
    setIsRetrying(true);
    try {
      await retryImport(accessToken, job.id);
      showToast('Import re-queued. This can take a few minutes.', 'success');
      _onClose?.();
    } catch (error) {
      logger.error('Retry import failed from attention modal', {
        error: error instanceof Error ? error.message : String(error),
        migrationId: job.id
      });
      showToast('Failed to re-queue the import. Please try again.', 'error');
    } finally {
      setIsRetrying(false);
    }
  };

  // --- Status Progress Tracker ---
  type StepStatus = 'completed' | 'current' | 'failed' | 'approval' | 'pending';

  interface ProgressStep {
    label: string;
    subLabel?: string | undefined;
    description: string;
    status: StepStatus;
  }

  const getMigrationSteps = (): ProgressStep[] => {
    const incomingStatus = job.status.toString();
    const failedStage = errorInfo?.stage;
    const transformationsSkipped = job.job2Transform?.status === 'Skipped';

    const exportStatus: StepStatus =
      failedStage === 'Export'
        ? 'failed'
        : incomingStatus === 'ExportFailed'
          ? 'failed'
          : ['ExportQueued', 'ExportInProgress'].includes(incomingStatus)
            ? 'current'
            : incomingStatus === 'ExportComplete'
              ? 'approval'
              // If a later stage failed, export must have completed successfully
              : (failedStage === 'Transformation' || failedStage === 'Import')
                ? 'completed'
                : [
                  'TransformationQueued',
                  'TransformationInProgress',
                  'TransformationComplete',
                  'TransformationFailed',
                  'ImportQueued',
                  'ImportInProgress',
                  'ImportFailed',
                  'ValidationFailed',
                  'Failed',
                  'failed',
                  'completed',
                  'PartiallyCompleted'
                ].includes(incomingStatus)
                  ? 'completed'
                  : 'pending';

    const transformStatus: StepStatus =
      failedStage === 'Transformation'
        ? 'failed'
        : incomingStatus === 'TransformationFailed'
          ? 'failed'
          : exportStatus === 'failed'
            ? 'pending'
            : ['TransformationQueued', 'TransformationInProgress'].includes(incomingStatus)
              ? 'current'
              : incomingStatus === 'TransformationComplete'
                ? 'approval'
                // If import stage failed, transform must have completed (or been skipped)
                : failedStage === 'Import'
                  ? 'completed'
                  : [
                    'ImportQueued',
                    'ImportInProgress',
                    'ImportFailed',
                    'ValidationFailed',
                    'Failed',
                    'failed',
                    'completed',
                    'PartiallyCompleted'
                  ].includes(incomingStatus)
                    ? 'completed'
                    : 'pending';

    const importStatus: StepStatus =
      failedStage === 'Import'
        ? 'failed'
        : incomingStatus === 'ImportFailed' || incomingStatus === 'ValidationFailed' || incomingStatus === 'Failed' || incomingStatus === 'failed'
          ? 'failed'
          : exportStatus === 'failed' || transformStatus === 'failed'
            ? 'pending'
            : ['ImportQueued', 'ImportInProgress'].includes(incomingStatus)
              ? 'current'
              : ['completed', 'PartiallyCompleted'].includes(incomingStatus)
                ? 'completed'
                : 'pending';

    const completeStatus: StepStatus =
      exportStatus === 'failed' || transformStatus === 'failed' || importStatus === 'failed'
        ? 'pending'
        : incomingStatus === 'PartiallyCompleted'
          ? 'current'
          : incomingStatus === 'completed'
            ? 'completed'
            : 'pending';

    return [
      {
        label: 'Export',
        description:
          exportStatus === 'failed'
            ? 'Export encountered errors'
            : exportStatus === 'completed'
              ? 'Resources exported successfully'
              : 'Resources will be exported from the source organization',
        status: exportStatus
      },
      ...(!transformationsSkipped ? [{
        label: 'Transform',
        description:
          transformStatus === 'failed'
            ? 'Transformation encountered errors'
            : transformStatus === 'completed'
              ? 'Transformations applied successfully'
              : 'Transformations will be applied to exported resources',
        status: transformStatus
      }] : []),
      {
        label: 'Import',
        description:
          importStatus === 'failed'
            ? 'Import encountered errors'
            : importStatus === 'completed'
              ? 'Resources imported successfully'
              : 'Resources will be imported into the target organization',
        status: importStatus
      },
      {
        label: 'Complete',
        subLabel: completeStatus === 'current' ? 'Partial' : undefined,
        description:
          completeStatus === 'completed'
            ? 'Migration completed successfully'
            : completeStatus === 'current'
              ? 'Migration partially completed'
              : 'Migration will be marked complete',
        status: completeStatus
      }
    ];
  };

  const getSnapshotSteps = (): ProgressStep[] => {
    const incomingStatus = job.status.toString();
    const failedStage = errorInfo?.stage;

    const snapshotExportStatus: StepStatus =
      failedStage === 'Export'
        ? 'failed'
        : incomingStatus === 'ExportFailed'
          ? 'failed'
          : ['ExportQueued', 'ExportInProgress'].includes(incomingStatus)
            ? 'current'
            : incomingStatus === 'SnapshotCompleted'
              ? 'completed'
              : 'pending';

    const completeStatus: StepStatus =
      snapshotExportStatus === 'failed'
        ? 'pending'
        : incomingStatus === 'SnapshotCompleted'
          ? 'completed'
          : 'pending';

    return [
      {
        label: 'Export',
        description:
          snapshotExportStatus === 'failed'
            ? 'Snapshot encountered errors'
            : snapshotExportStatus === 'completed'
              ? 'Resources captured from the source organization'
              : 'Resources will be captured from the source organization',
        status: snapshotExportStatus
      },
      {
        label: 'Complete',
        description:
          completeStatus === 'completed'
            ? 'Snapshot completed successfully'
            : 'Snapshot will be marked complete once all resources are captured',
        status: completeStatus
      }
    ];
  };

  const getRollbackSteps = (): ProgressStep[] => {
    const incomingStatus = job.status.toString();
    const failedStage = errorInfo?.stage;

    const planStatus: StepStatus =
      failedStage === 'Rollback' && (job.job4RollbackSetup?.errors?.length ?? 0) > 0
        ? 'failed'
        : ['RollbackQueued', 'RollbackSetupInProgress'].includes(incomingStatus)
          ? 'current'
          : incomingStatus === 'RollbackPlanAwaitingApproval'
            ? 'approval'
            : ['RollbackInProgress', 'RollbackComplete', 'RollbackPartiallyCompleted', 'RollbackFailed'].includes(incomingStatus)
              ? 'completed'
              : 'pending';

    const applyStatus: StepStatus =
      planStatus === 'failed'
        ? 'pending'
        : incomingStatus === 'RollbackFailed'
          ? 'failed'
          : incomingStatus === 'RollbackInProgress'
            ? 'current'
            : ['RollbackComplete', 'RollbackPartiallyCompleted'].includes(incomingStatus)
              ? 'completed'
              : 'pending';

    const completeStatus: StepStatus =
      planStatus === 'failed' || applyStatus === 'failed'
        ? 'pending'
        : incomingStatus === 'RollbackPartiallyCompleted'
          ? 'current'
          : incomingStatus === 'RollbackComplete'
            ? 'completed'
            : 'pending';

    return [
      {
        label: 'Plan',
        description:
          planStatus === 'failed'
            ? 'Rollback setup encountered errors'
            : planStatus === 'approval'
              ? 'Rollback plan awaiting your approval'
              : planStatus === 'completed'
                ? 'Rollback plan generated successfully'
                : 'Generating rollback plan from migration snapshot',
        status: planStatus
      },
      {
        label: 'Apply',
        description:
          applyStatus === 'failed'
            ? 'Rollback encountered errors during apply'
            : applyStatus === 'completed'
              ? 'Rollback changes applied successfully'
              : 'Applying rollback changes to target organization',
        status: applyStatus
      },
      {
        label: 'Complete',
        subLabel: completeStatus === 'current' ? 'Partial' : undefined,
        description:
          completeStatus === 'completed'
            ? 'Rollback completed successfully'
            : completeStatus === 'current'
              ? 'Rollback partially completed'
              : 'Rollback will be marked complete once all resources are restored',
        status: completeStatus
      }
    ];
  };

  const isRollbackStatus =
    typeof job.status === 'string' && (job.status as string).startsWith('Rollback');

  const progressSteps = isRollbackStatus
    ? getRollbackSteps()
    : job.isSnapshot
      ? getSnapshotSteps()
      : getMigrationSteps();

  const stepBg: Record<StepStatus, string> = {
    completed: 'bg-green-500 dark:bg-green-600',
    current: 'bg-blue-500 dark:bg-blue-600',
    failed: 'bg-red-500 dark:bg-red-600',
    approval: 'bg-orange-500 dark:bg-orange-600',
    pending: 'bg-gray-300 dark:bg-gray-600'
  };

  const stepIcon = (status: StepStatus, index: number) => {
    if (status === 'completed')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'failed')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'approval')
      return (
        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      );
    if (status === 'current')
      return <span className="w-2 h-2 rounded-full bg-white animate-pulse block" />;
    return <span className="text-xs font-bold text-gray-500 dark:text-gray-400">{index + 1}</span>;
  };

  return (
    <FindingsDisclosureProvider storagePrefix={`attn:${job.id}`}>
      <div className="space-y-6">
        {/* Name Block */}
        <div className={`p-4 rounded-lg shadow-md bg-white dark:bg-gray-800 border-l-8 ${isPlanningFailure ? 'border-amber-500 dark:border-amber-400' : errorInfo ? 'border-red-500 dark:border-red-400' : isWarningState ? 'border-yellow-500 dark:border-yellow-400' : 'border-green-500 dark:border-green-400'} border border-gray-200 dark:border-gray-700`}>
          <div className={`${ui.text.xs} font-bold uppercase tracking-wider mb-1 ${isPlanningFailure ? 'text-amber-600 dark:text-amber-400' : errorInfo ? 'text-red-600 dark:text-red-400' : isWarningState ? 'text-yellow-600 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
            {typeLabel} Name
          </div>
          <div className={`${ui.text.sectionTitle} font-bold break-words text-gray-900 dark:text-gray-100`}>
            {job.name}
          </div>
        </div>

        {/* Status Header */}
        <div
          className={`rounded-lg ${ui.page.cardPadding} ${isPlanningFailure ? 'bg-amber-100 dark:bg-amber-900/30 border-2 border-amber-400 dark:border-amber-600' : errorInfo ? 'bg-red-100 dark:bg-red-900/30 border-2 border-red-400 dark:border-red-600' : isWarningState ? 'bg-yellow-50 dark:bg-yellow-900/30 border-2 border-yellow-400 dark:border-yellow-600' : 'bg-green-100 dark:bg-green-900/30 border-2 border-green-400 dark:border-green-600'}`}
        >
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${ui.gap.lg}`}>
              <div
                className={`${ui.svg.xl} rounded-full flex items-center justify-center ${isPlanningFailure ? 'bg-amber-500' : errorInfo ? 'bg-red-500' : isWarningState ? 'bg-yellow-500' : 'bg-green-500'}`}
              >
                <svg className={`${ui.svg.lg} text-white`} fill="currentColor" viewBox="0 0 20 20">
                  {errorInfo ? (
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  ) : isWarningState ? (
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  ) : (
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  )}
                </svg>
              </div>
              <div>
                <h3
                  className={`font-black ${ui.text.sectionTitle} ${isPlanningFailure ? 'text-amber-900 dark:text-amber-200' : errorInfo ? 'text-red-900 dark:text-red-200' : isWarningState ? 'text-yellow-900 dark:text-yellow-200' : 'text-green-900 dark:text-green-200'}`}
                >
                  {isPlanningFailure
                    ? 'Import Failed'
                    : errorInfo
                      ? `${errorInfo.stage} ${(job.status === 'RollbackPartiallyCompleted' || job.status === 'PartiallyCompleted' || Number(job.status) === 10) ? 'Partially Completed' : 'Failed'}`
                      : isWarningState
                        ? warningHeading
                        : isRollbackStatus
                          ? 'Rollback Details'
                          : 'Migration Details'}
                </h3>
                <p
                  className={`${ui.text.label} mt-1 ${isPlanningFailure ? 'text-amber-700 dark:text-amber-300' : errorInfo ? 'text-red-700 dark:text-red-300' : isWarningState ? 'text-yellow-700 dark:text-yellow-300' : 'text-green-700 dark:text-green-300'}`}
                >
                  {isPlanningFailure
                    ? 'Migration Failed — No Changes Applied'
                    : errorInfo
                      ? job.status === 'RollbackPartiallyCompleted'
                        ? 'Some resources could not be restored'
                        : (job.status === 'PartiallyCompleted' || Number(job.status) === 10)
                          ? 'Some resources imported successfully; others failed and require attention'
                          : 'Changes May Have Been Applied and Requires Attention'
                      : isWarningState
                        ? warningSubtext
                        : 'All systems operational'}
                </p>
              </div>
            </div>
            {errorInfo && (
              <span className={`${isPlanningFailure ? 'bg-amber-600' : 'bg-red-600'} text-white ${ui.button.sm} rounded-full font-bold`}>
                ERROR
              </span>
            )}
            {isWarningState && !errorInfo && (
              <span className={`bg-yellow-500 text-white ${ui.button.sm} rounded-full font-bold`}>
                WARNING
              </span>
            )}
          </div>
        </div>

        {/* Dry-run preview — no changes were applied, so this is a preview, not a real import.
          Rollback is hidden for this state (nothing to revert). */}
        {isDryRunPreview && (
          <div
            className={`flex items-start ${ui.gap.sm} bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-300 dark:border-indigo-700 rounded-lg ${ui.page.sectionPadding} ${ui.mb.title}`}
            role="status"
          >
            <svg
              className={`${ui.svg.sm} text-indigo-600 dark:text-indigo-400 shrink-0 mt-0.5`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <div className="min-w-0">
              <p className={`${ui.text.label} font-bold text-indigo-800 dark:text-indigo-200`}>
                Dry-run preview completed
              </p>
              <p className={`${ui.text.xs} text-indigo-700 dark:text-indigo-300 mt-0.5 break-words`}>
                No changes were applied to the target org — this was a preview only. The confidence and
                remediation work-list are shown below.
              </p>
            </div>
          </div>
        )}

        {/* Dry-run confidence + remediation work-list. Rendered here (not only in the View modal) so a
          dry-run that routes to the Attention view still shows the preview the user ran it for. */}
        {isDryRunPreview && accessToken && (
          <>
            <MigrationPreviewPanel token={accessToken} migrationId={job.id} className={ui.mb.title} />
            <RunRealImportButton
              token={accessToken}
              migrationId={job.id}
              onImportQueued={_onClose}
              className={ui.mb.title}
            />
          </>
        )}

        {/* Expand all / Collapse all — controls every collapsible section below (findings banners,
          selected resources, history) and persists each section's state per migration. */}
        <FindingsDisclosureToolbar />

        {/* Division-to-Division remapping warnings — surfaces skipped division_id remaps
          ([DIV-MAP]/[DIV-PRUNE]) and unmatched org-scoped resources ([RESOURCE-CHECK])
          from the transform/import jobs so the user sees them on the post-import view. */}
        <MigrationWarningsBanner
          warnings={divisionWarnings}
          title="Division migration warnings"
          sectionId="division-warnings"
        />

        {/* End-to-end reconciliation — surfaces resources that were requested but did not land in the
          target org ([RECONCILIATION] rollup + per-type breakdown from the import job), so a partial
          migration is never presented as fully complete. */}
        <NotFullyMigratedBanner warnings={reconciliationWarnings} />

        {/* Target-org readiness prerequisites — TTS voices, duplicate names, division cap,
          emergency-number collisions, and deprecated flow types the operator must resolve. */}
        <TargetReadinessPanel warnings={readinessWarnings} />

        {/* Partial-success clarity — how many resources actually imported vs. failed, so a partial
          import shows what DID land alongside the errors below. Rendered only when a per-resource
          breakdown exists and at least one resource failed (a fully clean import needs no callout). */}
        {!job.isSnapshot && importResultSummary && importResultSummary.failed > 0 && (
          <div
            className={`flex items-center flex-wrap ${ui.gap.sm} bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg ${ui.page.sectionPadding} ${ui.mb.title}`}
            role="status"
          >
            <span className={`${ui.text.label} font-semibold text-gray-800 dark:text-gray-100`}>
              {importResultSummary.imported} of {importResultSummary.total} resources imported
            </span>
            <span
              className={`inline-flex items-center ${ui.badge.padding} rounded-full ${ui.text.badge} font-semibold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300`}
            >
              {importResultSummary.imported} succeeded
            </span>
            <span
              className={`inline-flex items-center ${ui.badge.padding} rounded-full ${ui.text.badge} font-semibold bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300`}
            >
              {importResultSummary.failed} failed
            </span>
          </div>
        )}

        {/* Manual-remediation banner — cascade-excluded resources that need operator action
          ([CASCADE] / [MANUAL-STEPS] / [FLOW-REPAIR]) on completed migrations. */}
        {completionWarnings.length > 0 && (
          <CollapsibleFindingsSection
            variant="yellow"
            title="Warning Action Required"
            iconPath="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            count={completionWarnings.length}
            sectionId="manual-steps"
            summary="This migration completed, but the items below may require manual follow-up."
          >
            <p className={`${ui.text.xs} text-yellow-700 dark:text-yellow-300 ${ui.mb.sm}`}>
              This migration completed, but the items below may require manual follow-up.
            </p>
            <ul className={`flex flex-col ${ui.gap.xs}`}>
              {completionWarnings.map((warning, index) => (
                <li
                  key={index}
                  className={`${ui.text.xs} text-yellow-800 dark:text-yellow-200 break-words`}
                >
                  {warning}
                </li>
              ))}
            </ul>
          </CollapsibleFindingsSection>
        )}

        {/* Contextual Warning / Impact Summary — shown before migration name so users immediately see what happened */}
        {errorInfo && (
          <>
            {isPlanningFailure ? (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg
                    className={`${ui.svg.sm} text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <h4 className={`${ui.text.label} font-bold text-amber-800 dark:text-amber-300 mb-1`}>
                      No changes were applied
                    </h4>
                    <p className={`${ui.text.body} text-amber-700 dark:text-amber-400 mb-2`}>
                      This migration failed before any updates were made to your Target organization. Your environment remains unchanged.
                    </p>
                    <ul className={`${ui.text.body} text-amber-700 dark:text-amber-400 space-y-1 list-disc list-inside`}>
                      <li>Review your migration configuration </li>
                      <li>View error details and logs</li>
                      <li>Verify  your Genesys organizations' credentials and settings</li>
                      <li>Fix any issues and retry the migration by clicking the Edit Approve Export button below.</li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : isExecutionFailure ? (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-400 dark:border-red-600 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg
                    className={`${ui.svg.sm} text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <h4 className={`${ui.text.label} font-bold text-red-800 dark:text-red-300 mb-1`}>
                      Changes may have been applied to your target organization
                    </h4>
                    <p className={`${ui.text.body} text-red-700 dark:text-red-400 mb-2`}>
                      An unexpected error has occurred. This migration failed after import changes began. Some resources in your Target environment may have been created or modified.
                    </p>
                    <ul className={`${ui.text.body} text-red-700 dark:text-red-400 space-y-1 list-disc list-inside`}>
                      <li>Review your environment for unexpected changes</li>
                      <li>Validate impacted resources </li>
                      <li>Compare pre- and post-migration states by clicking the Compare Snapshots button below</li>
                      <li>Consider rolling back this migration </li>
                    </ul>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-300 dark:border-red-700 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <svg
                    className={`${ui.svg.sm} text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="flex-1">
                    <h4
                      className={`${ui.text.label} font-bold text-red-800 dark:text-red-300 mb-1`}
                    >
                      Error Action Required
                    </h4>
                    <p className={`${ui.text.body} text-red-700 dark:text-red-400`}>
                      {errorInfo.buttonText ? (
                        <>
                          The {errorInfo.stage.toLowerCase()} stage encountered errors. Please click the
                          <span className="font-semibold"> {errorInfo.buttonText} button</span> below to
                          navigate back to the previous step and resolve these issues before proceeding.
                        </>
                      ) : (
                        <>
                          The {errorInfo.stage.toLowerCase()} stage encountered errors. Review the error
                          log below for details.
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
        {warningInfo && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <svg
                className={`${ui.svg.sm} text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="flex-1">
                <h4 className={`${ui.text.label} font-bold text-yellow-800 dark:text-yellow-300 mb-2`}>
                  {warningInfo.stage} Warnings
                </h4>
                <ul className="space-y-1 mb-2">
                  {warningInfo.warnings.map((w, i) => (
                    <li key={i} className={`${ui.text.body} text-yellow-700 dark:text-yellow-400`}>{w}</li>
                  ))}
                </ul>
                <ul className={`${ui.text.body} text-yellow-700 dark:text-yellow-400 space-y-1 list-disc list-inside`}>
                  <li>Use <span className="font-semibold">Compare Snapshots</span> below to review what changed against your pre-migration snapshot</li>
                  {rollbackEnabled && (
                    <li>Use <span className="font-semibold">Initiate Rollback</span> to revert your target organization to its pre-migration state</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
        {errorInfo && !isPlanningFailure && !job.isSnapshot && (
          <div className="rounded-xl border border-red-200 dark:border-red-800 overflow-hidden">
            {rollbackEnabled && daysRemainingInWindow !== null && (
              <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${rollbackWindowExpired
                ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                : daysRemainingInWindow <= 1
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                  : daysRemainingInWindow <= 2
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400'
                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                }`}>
                <svg className={`${ui.svg.sm} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`${ui.text.xs} font-medium`}>
                  {rollbackWindowExpired
                    ? '"Initiate Rollback" is no longer available'
                    : daysRemainingInWindow === 0
                      ? 'Last day to use the "Initiate Rollback" button'
                      : `"Initiate Rollback" button available for ${daysRemainingInWindow} more day${daysRemainingInWindow !== 1 ? 's' : ''}`
                  }
                </span>
              </div>
            )}
            {rollbackEnabled && job.snapshotBlobUrl && (
              <div className={`flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 ${ui.text.xs} text-gray-600 dark:text-gray-400`}>
                <span className="truncate" title={job.snapshotBlobUrl}>
                  Rollback snapshot: <span className="font-mono">{job.snapshotBlobUrl}</span>
                </span>
                <button
                  type="button"
                  onClick={handleCopySnapshotPath}
                  className="shrink-0 font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                  title="Copy the pre-migration snapshot path for auditing"
                >
                  Copy path
                </button>
              </div>
            )}
            <div className="p-4 bg-red-50/50 dark:bg-red-900/10">
              <div className={`grid ${!rollbackEnabled || rollbackWindowExpired ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                <button
                  onClick={() => navigate('/SnapshotComparisonTool', { state: { job } })}
                  className={`w-full bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-red-700 dark:text-red-400 border-2 border-red-400 dark:border-red-600 font-semibold ${ui.button.lg} rounded-lg transition shadow flex items-center justify-center gap-2`}
                >
                  <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Compare Snapshots</span>
                </button>
                {rollbackEnabled && !rollbackWindowExpired && (
                  <button
                    onClick={() => navigate(`/RollbackInitiation?migrationId=${encodeURIComponent(job.id)}`, { state: { job } })}
                    className={`w-full bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white font-semibold ${ui.button.lg} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
                  >
                    <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                    </svg>
                    <span>Initiate Rollback</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {(warningInfo || completionWarnings.length > 0) && !job.isSnapshot && !(errorInfo && !isPlanningFailure) && (
          <div className="rounded-xl border border-yellow-200 dark:border-yellow-800 overflow-hidden">
            {rollbackEnabled && daysRemainingInWindow !== null && (
              <div className={`flex items-center gap-2 px-4 py-2.5 border-b ${rollbackWindowExpired
                ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400'
                : daysRemainingInWindow <= 1
                  ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                  : daysRemainingInWindow <= 2
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400'
                    : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                }`}>
                <svg className={`${ui.svg.sm} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`${ui.text.xs} font-medium`}>
                  {rollbackWindowExpired
                    ? '"Initiate Rollback" is no longer available'
                    : daysRemainingInWindow === 0
                      ? 'Last day to use the "Initiate Rollback" button'
                      : `"Initiate Rollback" button available for ${daysRemainingInWindow} more day${daysRemainingInWindow !== 1 ? 's' : ''}`
                  }
                </span>
              </div>
            )}
            {rollbackEnabled && job.snapshotBlobUrl && (
              <div className={`flex items-center justify-between gap-2 px-4 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 ${ui.text.xs} text-gray-600 dark:text-gray-400`}>
                <span className="truncate" title={job.snapshotBlobUrl}>
                  Rollback snapshot: <span className="font-mono">{job.snapshotBlobUrl}</span>
                </span>
                <button
                  type="button"
                  onClick={handleCopySnapshotPath}
                  className="shrink-0 font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                  title="Copy the pre-migration snapshot path for auditing"
                >
                  Copy path
                </button>
              </div>
            )}
            <div className="p-4 bg-yellow-50/50 dark:bg-yellow-900/10">
              <div className={`grid ${!rollbackEnabled || rollbackWindowExpired ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                <button
                  onClick={() => navigate('/SnapshotComparisonTool', { state: { job } })}
                  className={`w-full bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-yellow-700 dark:text-yellow-400 border-2 border-yellow-400 dark:border-yellow-600 font-semibold ${ui.button.lg} rounded-lg transition shadow flex items-center justify-center gap-2`}
                >
                  <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span>Compare Snapshots</span>
                </button>
                {rollbackEnabled && !rollbackWindowExpired && (
                  <button
                    onClick={() => navigate(`/RollbackInitiation?migrationId=${encodeURIComponent(job.id)}`, { state: { job } })}
                    className={`w-full bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700 text-white font-semibold ${ui.button.lg} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
                  >
                    <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                    </svg>
                    <span>Initiate Rollback</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Info Cards Grid */}
        <div className="space-y-4">
          {/* Progress Tracker */}
          <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            <div
              className={`${ui.text.label} font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3`}
            >
              {typeLabel} Progress
            </div>
            <div className="relative flex items-start gap-0">
              {progressSteps.map((step, index) => (
                <div key={step.label} className="flex-1 flex flex-col items-center relative">
                  {/* Connector line */}
                  {index < progressSteps.length - 1 && (
                    <div
                      className={`absolute top-3.5 left-1/2 w-full h-0.5 z-0 ${step.status === 'completed' ? 'bg-green-400 dark:bg-green-600' : step.status === 'failed' ? 'bg-red-400 dark:bg-red-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                    />
                  )}

                  {/* Circle */}
                  <div
                    className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${stepBg[step.status]} ${step.status === 'current' ? 'ring-2 ring-offset-2 ring-blue-400 dark:ring-offset-gray-800' : ''} ${step.status === 'failed' ? 'ring-2 ring-offset-2 ring-red-400 dark:ring-offset-gray-800' : ''}`}
                  >
                    {stepIcon(step.status, index)}
                  </div>

                  {/* Label + description */}
                  <div className="mt-2 text-center px-1">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      <div
                        className={`${ui.text.label} font-semibold ${step.status === 'completed'
                          ? 'text-green-700 dark:text-green-400'
                          : step.status === 'current'
                            ? 'text-blue-700 dark:text-blue-300'
                            : step.status === 'failed'
                              ? 'text-red-600 dark:text-red-400'
                              : step.status === 'approval'
                                ? 'text-orange-600 dark:text-orange-400'
                                : 'text-gray-400 dark:text-gray-500'
                          }`}
                      >
                        {step.label}
                      </div>
                      {step.subLabel && (
                        <span
                          className={`inline-block text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none ${step.subLabel === 'Queued'
                            ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300'
                            : step.subLabel === 'Partial'
                              ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300'
                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                            }`}
                        >
                          {step.subLabel}
                        </span>
                      )}
                    </div>
                    <div
                      className={`${ui.text.label} text-gray-500 dark:text-gray-400 mt-0.5 leading-tight`}
                    >
                      {step.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Schedule Display */}
          {scheduleDisplay && (
            <div
              className={`flex items-center ${ui.gap.lg} ${ui.page.sectionPadding} bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700`}
            >
              <div className="p-2 bg-blue-100 dark:bg-blue-800/50 rounded-lg">
                <svg
                  className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div>
                <div className={`${ui.text.label} font-medium text-blue-700 dark:text-blue-300 mb-1`}>
                  Recurring Schedule
                </div>
                <div className={`font-bold ${ui.text.label} text-blue-900 dark:text-blue-100`}>
                  {scheduleDisplay}
                </div>
                {nextRunDisplay && (
                  <div className={`${ui.text.xs} text-blue-700 dark:text-blue-300 mt-1`}>
                    Next run: {nextRunDisplay}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <svg
                  className={`${ui.svg.sm} text-blue-600 dark:text-blue-400`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                  <path
                    fillRule="evenodd"
                    d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
                  Type
                </div>
                <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
                  {typeLabel}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <svg
                  className={`${ui.svg.sm} text-purple-600 dark:text-purple-400`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
                  Created By
                </div>
                <div
                  className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100 md:truncate xl:whitespace-normal xl:overflow-visible`}
                  title={job.createdBy}
                >
                  {job.createdBy}
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <svg
                  className={`${ui.svg.sm} text-orange-600 dark:text-orange-400`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}>
                  Created At
                </div>
                <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
                  {formatUtcTimestampLocal(job.createdAtUtc)}
                </div>
              </div>
            </div>

            {job.updatedAtUtc && (
              <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
                  <svg
                    className={`${ui.svg.sm} text-teal-600 dark:text-teal-400`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={`${ui.text.label} font-medium text-gray-500 dark:text-gray-400 mb-1`}
                  >
                    Last Updated
                  </div>
                  <div className={`font-semibold ${ui.text.label} text-gray-900 dark:text-gray-100`}>
                    {formatUtcTimestampLocal(job.updatedAtUtc)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Selected Resources Section */}
        <CollapsibleFindingsSection
          variant="neutral"
          title="Selected Resources"
          sectionId="selected-resources"
          defaultOpen
          {...(!job.exportAll && resourcesByType.size > 0 ? { count: resourcesByType.size } : {})}
          titleAccessory={
            <Tooltip
              position="right"
              content={
                job.isSnapshot
                  ? 'The resources that were selected when this snapshot was configured.'
                  : 'The resources that were selected when this migration was configured.'
              }
            />
          }
        >
          {loadingResources ? (
            <div className="flex flex-col items-center gap-2 py-6">
              <Spinner />
              <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>Loading resources...</p>
            </div>
          ) : exportedResourcesError ? (
            <div className="py-6 text-center">
              <p className={`${ui.text.body} text-gray-500 dark:text-gray-400`}>Failed to load resources</p>
              <p className={`${ui.text.xs} text-gray-400 dark:text-gray-500 mt-1`}>{exportedResourcesError.message}</p>
            </div>
          ) : job.exportAll ? (
            <div
              className={`flex items-center ${ui.gap.md} py-4 px-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700`}
            >
              <svg
                className={`${ui.svg.md} text-blue-600 dark:text-blue-400 flex-shrink-0`}
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <div className={`${ui.text.body} font-semibold text-blue-900 dark:text-blue-100`}>
                  Full {job.isSnapshot ? 'Snapshot' : 'Migration'}
                </div>
                <div className={`${ui.text.xs} text-blue-700 dark:text-blue-300 mt-0.5`}>
                  All supported resource types are included
                </div>
              </div>
            </div>
          ) : resourcesByType.size > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-80 overflow-y-auto">
              {Array.from(resourcesByType.entries()).map(([type, entries]) => (
                <details key={type} className="group bg-white dark:bg-gray-800">
                  <summary
                    className={`flex items-center justify-between ${ui.page.rowPadding} ${ui.gap.md} cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-900 transition`}
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        className={`${ui.svg.sm} text-gray-400 transition-transform group-open:rotate-90`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className={`${ui.text.body} font-semibold text-gray-900 dark:text-gray-100`}>
                        {getTypeLabelByApiName(toFriendlyName(type))}
                      </span>
                    </div>
                    {entries.length === 1 && entries[0]?.primary === 'Attempted to export all resources' ? (
                      <span
                        className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 ${ui.badge.padding} rounded-full whitespace-nowrap`}
                      >
                        All*
                      </span>
                    ) : (
                      <span
                        className={`${ui.text.xs} font-bold text-white bg-blue-600 dark:bg-blue-700 ${ui.badge.padding} rounded-full whitespace-nowrap`}
                      >
                        {entries.length}
                      </span>
                    )}
                  </summary>
                  <div className={`${ui.page.rowPadding} bg-gray-50 dark:bg-gray-900 max-h-48 overflow-y-auto`}>
                    <div className="space-y-1">
                      {entries.map((entry) => (
                        <div
                          key={entry.key}
                          className={`flex items-start gap-2 py-1 min-w-0 ${ui.text.label} text-gray-700 dark:text-gray-300`}
                        >
                          <svg
                            className={`${ui.svg.sm} text-green-500 flex-shrink-0 mt-0.5`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <div className="min-w-0">
                            <div className="break-all">{entry.primary}</div>
                            {entry.secondary && (
                              <div className={`${ui.text.xs} text-gray-500 dark:text-gray-400 mt-0.5 break-all`}>
                                {entry.secondary}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </details>
              ))}
            </div>
          ) : (
            <div className="py-6 text-center text-gray-400 dark:text-gray-500 italic">
              <p className={`${ui.text.body}`}>No resources selected</p>
            </div>
          )}
        </CollapsibleFindingsSection>

        {/* History Table Section - Show for scheduled jobs (snapshot or migration) */}
        {job.snapshotCronExpression && (
          <CollapsibleFindingsSection
            variant="neutral"
            title={job.isSnapshot ? 'Snapshot History' : 'Migration History'}
            iconPath="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            sectionId="history"
            defaultOpen
            {...(job.snapshotHistory && job.snapshotHistory.length > 0 ? { count: job.snapshotHistory.length } : {})}
          >
            {(job.snapshotHistory && job.snapshotHistory.length > 0) ? (
              job.isSnapshot ? (
                <SnapshotHistoryTable history={job.snapshotHistory} migrationId={job.id} />
              ) : (
                <MigrationHistoryTable history={job.snapshotHistory} migrationId={job.id} />
              )
            ) : (
              <div
                className={`flex items-center justify-center py-6 ${ui.text.label} text-gray-400 dark:text-gray-500 italic`}
              >
                No history available
              </div>
            )}
          </CollapsibleFindingsSection>
        )}

        {/* Sync Now — org sync migrations only */}
        {!job.isSnapshot && job.snapshotCronExpression && job.targetOrgId && onSyncNow && (
          <div className={`flex items-center justify-between ${ui.page.sectionPadding} bg-teal-50 dark:bg-teal-900/20 rounded-lg border border-teal-200 dark:border-teal-700`}>
            <div className={`flex items-start ${ui.gap.md}`}>
              <div className="p-2 bg-teal-100 dark:bg-teal-800/50 rounded-lg shrink-0">
                <svg className={`${ui.svg.sm} text-teal-600 dark:text-teal-400`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <div className={`${ui.text.label} font-semibold text-teal-800 dark:text-teal-200`}>Manual Sync</div>
                <div className={`${ui.text.xs} text-teal-600 dark:text-teal-400 mt-0.5`}>
                  Trigger a sync run immediately, outside the regular schedule. After resolving this failure, use this to verify the fix before the next scheduled run.
                </div>
              </div>
            </div>
            <button
              onClick={onSyncNow}
              disabled={syncNowLoading}
              className={`shrink-0 ml-4 rounded-lg bg-teal-600 hover:bg-teal-700 dark:bg-teal-700 dark:hover:bg-teal-600 text-white font-semibold ${ui.button.md} shadow transition flex items-center ${ui.gap.xs} ${syncNowLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
              type="button"
            >
              {syncNowLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                  <span>Syncing...</span>
                </>
              ) : (
                <>
                  <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                  <span>Sync Now</span>
                </>
              )}
            </button>
          </div>
        )}

        {/* Error Message Area */}
        {errorInfo && (
          <>
            <div className="bg-black dark:bg-black rounded-lg p-4 border border-gray-700">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-2 mb-3">
                <span className={`${ui.text.label} font-bold text-red-400 uppercase tracking-wider flex-shrink-0`}>
                  Error Log
                </span>
                <div className="hidden sm:block flex-1 h-px bg-red-900"></div>
                <button
                  onClick={handleCopyErrorLog}
                  className={`w-full sm:w-auto flex items-center justify-center gap-2 rounded-lg font-semibold transition-all duration-300 shadow-lg ${ui.button.copyError} ${isCopied
                    ? 'bg-green-500 hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 text-white scale-105'
                    : 'bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white hover:scale-105'
                    }`}
                  title={isCopied ? 'Copied!' : 'Copy error log to clipboard'}
                >
                  {isCopied ? (
                    <>
                      <svg
                        className={`${ui.svg.copyError} animate-bounce`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <span className="font-bold">Copied!</span>
                    </>
                  ) : (
                    <>
                      <svg
                        className={ui.svg.copyError}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth={2.5}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                      <span>Copy Error</span>
                    </>
                  )}
                </button>
              </div>
              <pre className={`font-mono whitespace-pre-wrap break-words ${ui.widthAndHeight.errorLog} overflow-y-auto ${ui.text.label} text-green-400`} >
                {Array.isArray(errorInfo.errors) ? errorInfo.errors.join('\n') : errorInfo.errors}
              </pre>
            </div>
            {errorInfo.route === '/ApproveExportPage' && (
              <div className="rounded-xl border border-red-200 dark:border-red-800 overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-2.5 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
                  <svg className={`${ui.svg.sm} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className={`${ui.text.xs} font-medium`}>
                    Review and re-approve your export selections, then retry this migration.
                  </span>
                </div>
                <div className="p-4 bg-red-50/50 dark:bg-red-900/10">
                  <button
                    onClick={handleNavigateToFix}
                    className={`w-full bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white font-semibold ${ui.button.lg} rounded-lg transition shadow-lg flex items-center justify-center gap-2`}
                    type="button"
                  >
                    <svg className={`${ui.svg.sm}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    <span>Edit Approve Export</span>
                  </button>
                </div>
              </div>
            )}
            {errorInfo.stage === 'Import' && !job.isSnapshot && (() => {
              const guidance = classifyRetrySafety(job.job3Import?.failurePhase ?? null);
              const tone =
                guidance.safety === 'safe'
                  ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                  : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300';
              return (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div className={`px-4 py-2.5 border-b ${tone}`}>
                    <div className={`${ui.text.label} font-semibold`}>{guidance.title}</div>
                    <div className={`${ui.text.xs} mt-1`}>{guidance.message}</div>
                  </div>
                  <div className={`${ui.page.sectionPadding} bg-gray-50/60 dark:bg-gray-800/40`}>
                    <div className={`${ui.text.xs} text-gray-500 dark:text-gray-400 ${ui.mb.sm}`}>
                      Retry re-runs the import using the resources you already exported and transformed —
                      no need to redo setup.
                    </div>
                    <button
                      onClick={handleRetryImport}
                      disabled={isRetrying}
                      className={`w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-600 text-white font-semibold ${ui.button.lg} rounded-lg transition shadow-lg flex items-center justify-center ${ui.gap.sm} disabled:opacity-60 disabled:cursor-not-allowed`}
                      type="button"
                    >
                      {isRetrying ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" aria-hidden="true">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                          </svg>
                          <span>Re-queuing…</span>
                        </>
                      ) : (
                        <>
                          <svg className={`${ui.svg.sm}`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                          </svg>
                          <span>Retry Import</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })()}
          </>
        )}
      </div>
    </FindingsDisclosureProvider>
  );
};
